import type { Question } from "../Sender/QuestionItem";
import {
  AD_CHAT_PROMPT_SCOPE,
  type AdChatPrompt,
  type AdChatPromptResult,
  titleOf,
} from "@hooks/useAdChatPrompts";
import { getRandomElements } from "@utils/getRandomElements";
import { buildAdChatMessage } from "@utils/onboarding/adChatMessage";
import { ETF_OVERLAP_REFERENCE_TYPE } from "@features/etf-overlap/utils/buildEtfOverlapChatMessage";

/** PC 与移动端的默认推荐问条数（现网 `getRandomElements(recommendQuestions, isPC ? 6 : 4)`）。 */
const DEFAULT_QUESTION_COUNT_PC = 6;
const DEFAULT_QUESTION_COUNT_MOBILE = 4;

/**
 * 广告问题 → 首屏 `Question`。`id` 即埋点 `prompt_id`，不要另造。
 *
 * `referenceType=etf_overlap`（EDG-561，后端配置驱动的重叠分析入口）不能用 `buildAdChatMessage` 的
 * `[](referenceType=X)\n{原文}` 拼法：那样正文非空，后端不认空正文入口。改为带 `intent`，
 * 点击由入口分流接管（有持仓直达 Chat 发空正文协议行，无持仓开引导），标题取后端返回的该项文案。
 */
export function adChatPromptToQuestion(
  prompt: AdChatPrompt,
  locale: string,
): Question {
  if (prompt.referenceType?.trim() === ETF_OVERLAP_REFERENCE_TYPE) {
    return {
      id: prompt.id,
      question: titleOf(prompt, locale),
      intent: "etf_overlap",
      scope: AD_CHAT_PROMPT_SCOPE,
      dataTrackingScope: `ad-chat-prompt-${prompt.id}`,
    };
  }
  return {
    id: prompt.id,
    question: titleOf(prompt, locale),
    // 展示问题原文，发送带协议行的整条（Task 5）
    sendText: buildAdChatMessage(prompt, locale),
    scope: AD_CHAT_PROMPT_SCOPE,
    dataTrackingScope: `ad-chat-prompt-${prompt.id}`,
  };
}

export interface ResolveChatQuestionsInput {
  isInApp: boolean;
  runtimeQuestions?: Question[];
  questions?: Question[];
  /** 广告问题组；`undefined` = 非广告会话或取数在飞 → 走现网默认路径。 */
  adChatResult?: AdChatPromptResult;
  recommendQuestions?: Question[];
  historyLoading: boolean;
  historyCount: number;
  isPC: boolean;
  locale: string;
  /** 随机挑选实现，仅供单测断言「广告态未被调用」。 */
  pickRandom?: (list: Question[], count: number) => Question[];
  /** EDG-591 要数据槽：只插在推荐接口那一档的第 1 张（广告 / runtime / props 档不插） */
  connectSlotQuestion?: Question | null;
}

/**
 * 首屏推荐问的优先级裁决（抽自 `ChatModalContent` 的 `resolvedQuestions`）。
 *
 * 现网：`runtimeQuestions`(App) > `questions`(props) > `getRandomElements(recommendQuestions)`
 * 本次：在最后一档**之前**插入广告态 —— 广告问题按配置顺序直出，**不经过随机挑选**
 * （随机会打乱配置顺序，那正是本链路要保住的东西）。
 *
 * 广告态未命中（回落 / 非广告会话 / 取数在飞）时，末档与现网**逐字一致**（F-4）。
 */
export function resolveChatQuestions(
  input: ResolveChatQuestionsInput,
): Question[] | undefined {
  const {
    isInApp,
    runtimeQuestions,
    questions,
    adChatResult,
    recommendQuestions,
    historyLoading,
    historyCount,
    isPC,
    locale,
    pickRandom = getRandomElements,
    connectSlotQuestion,
  } = input;

  // app 场景，优先使用 runtimeQuestions
  if (isInApp && runtimeQuestions && runtimeQuestions.length > 0) {
    return runtimeQuestions;
  }
  // web 场景，优先使用 questions
  if (questions && questions.length > 0) {
    return questions;
  }
  // 广告态：按配置顺序直出，不经 pickRandom
  if (adChatResult?.source === "material") {
    return adChatResult.prompts.map((p) => adChatPromptToQuestion(p, locale));
  }
  // historyLoading 结束后，且没有历史记录，优先使用 recommendQuestions
  if (
    !historyLoading &&
    historyCount === 0 &&
    recommendQuestions &&
    recommendQuestions.length > 0
  ) {
    const count = isPC
      ? DEFAULT_QUESTION_COUNT_PC
      : DEFAULT_QUESTION_COUNT_MOBILE;
    if (connectSlotQuestion) {
      return [
        connectSlotQuestion,
        ...pickRandom(recommendQuestions, count - 1),
      ];
    }
    return pickRandom(recommendQuestions, count);
  }
  return undefined;
}
